import React, { useState, useEffect } from 'react';
import { View, LearningModule } from '../types';
import Card from './common/Card';
import { ArrowLeftIcon, CheckIcon, XMarkIcon, ZoomInIcon } from './common/Icons';
import { formatAssetUrl } from '../services/apiService';

interface LearningModuleViewProps {
  onBack: () => void;
  onNavigate: (view: View, payload?: object) => void;
  planId: string;
  module: LearningModule;
  onComplete: (planId: string, moduleId: string, isCorrect: boolean) => void;
  isPreview?: boolean;
}

const LearningModuleView: React.FC<LearningModuleViewProps> = ({ onBack, module, planId, onComplete, isPreview = false }) => {
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null);
    
    // Lưu lại thông tin module đang học để khi quay lại Lộ trình có thể focus đúng vị trí
    useEffect(() => {
        if (module?.id && planId) {
            sessionStorage.setItem('lastViewedModuleId', module.id);
            sessionStorage.setItem('lastViewedPlanId', String(planId));
        }
    }, [module?.id, planId]);
    
    const isCorrect = isSubmitted && selectedOption === module.question.correctAnswer;
    
    const handleSubmit = () => {
        if (selectedOption === null || isSubmitted) return;
        setIsSubmitted(true);
        onComplete(planId, module.id, selectedOption === module.question.correctAnswer);
    };

    return (
        <div className="space-y-6">
            {isPreview && (
                <div className="bg-amber-50 border-2 border-amber-200 p-4 rounded-2xl flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 rounded-xl text-amber-600">
                             <span className="text-xl">⚠️</span>
                        </div>
                        <div>
                            <p className="font-black text-amber-800 uppercase text-xs tracking-wider">Chế độ xem trước của phụ huynh</p>
                            <p className="text-amber-600 text-[10px] font-bold">Bạn đang xem thử nội dung con sẽ học. Kết quả kiểm tra sẽ không được ghi nhận vào tiến độ học của con.</p>
                        </div>
                    </div>
                    <div className="hidden sm:block">
                        <span className="px-3 py-1 bg-amber-600 text-white text-[10px] font-black uppercase rounded-lg">Xem thử</span>
                    </div>
                </div>
            )}

            {zoomImageUrl && (
                <div 
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-90 p-4" 
                    onClick={() => setZoomImageUrl(null)}
                >
                    <button className="absolute top-4 right-4 text-white hover:text-gray-300">
                        <XMarkIcon className="h-8 w-8" />
                    </button>
                    <img 
                        src={zoomImageUrl} 
                        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" 
                        alt="Phóng to" 
                    />
                </div>
            )}
             <div>
                <button onClick={onBack} className="flex items-center space-x-2 text-sm font-medium text-gray-600 hover:text-gray-900">
                    <ArrowLeftIcon className="h-5 w-5" />
                    <span>Quay lại Lộ trình</span>
                </button>
            </div>

            <Card className="p-8">
                <div className="max-w-3xl mx-auto">
                    {/* Phần 1: Nội dung bài học */}
                    <div className="mb-10">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center font-bold text-lg">AI</div>
                             <h2 className="text-3xl font-bold text-gray-900">{module.title}</h2>
                        </div>
                        <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed bg-gray-50 p-6 rounded-lg">
                           {module.imageUrl && (
                               <div className="mb-6 rounded-xl overflow-hidden shadow-md max-w-md mx-auto aspect-video relative group">
                                   <img src={formatAssetUrl(module.imageUrl)} alt={module.title} className="w-full h-full object-cover" />
                                   <button 
                                        onClick={() => setZoomImageUrl(formatAssetUrl(module.imageUrl))}
                                        className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                                   >
                                       <ZoomInIcon className="h-5 w-5" />
                                   </button>
                               </div>
                           )}
                           <p>{module.content}</p>
                        </div>
                    </div>

                    {/* Phần 2: Câu hỏi kiểm tra */}
                    <div className="border-t pt-8">
                         <h3 className="text-xl font-semibold text-gray-800 mb-4">Câu hỏi nhanh!</h3>
                        
                        {module.question.image && (
                            <div className="mb-6 rounded-xl overflow-hidden shadow-sm max-w-md mx-auto aspect-video border bg-gray-50 relative group">
                                <img src={formatAssetUrl(module.question.image)} alt="Câu hỏi minh họa" className="w-full h-full object-cover" />
                                <button 
                                    onClick={() => setZoomImageUrl(formatAssetUrl(module.question.image))}
                                    className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                                >
                                    <ZoomInIcon className="h-5 w-5" />
                                </button>
                            </div>
                        )}

                        <p className="text-lg font-medium text-gray-800 mb-6">{module.question.text}</p>
                        
                        <div className={`grid gap-4 ${module.question.optionImages?.some(img => !!img) ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                            {module.question.options.map((option, index) => {
                                const isSelected = selectedOption === option;
                                const isCorrectAnswer = module.question.correctAnswer === option;
                                const optionImage = module.question.optionImages?.[index];

                                let buttonClass = 'border-gray-300 bg-white hover:bg-gray-100';
                                if (isSubmitted) {
                                    if (isCorrectAnswer) {
                                        buttonClass = 'border-green-500 bg-green-100 text-green-900 ring-2 ring-green-400';
                                    } else if (isSelected) {
                                        buttonClass = 'border-red-500 bg-red-100 text-red-900 ring-2 ring-red-400';
                                    } else {
                                        buttonClass = 'border-gray-300 bg-gray-50 text-gray-500';
                                    }
                                } else if (isSelected) {
                                    buttonClass = 'border-teal-500 bg-teal-50 ring-2 ring-teal-400';
                                }

                                return (
                                    <button
                                        key={index}
                                        onClick={() => !isSubmitted && setSelectedOption(option)}
                                        className={`w-full text-left p-4 border-2 rounded-lg flex flex-col transition-all ${isSubmitted ? 'cursor-default' : 'cursor-pointer' } ${buttonClass}`}
                                    >
                                        {optionImage && (
                                            <div className="w-full aspect-video rounded-md overflow-hidden mb-3 border bg-gray-200 relative group/optimg">
                                                <img src={formatAssetUrl(optionImage)} alt={option} className="w-full h-full object-cover" />
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setZoomImageUrl(formatAssetUrl(optionImage));
                                                    }}
                                                    className="absolute top-1 right-1 p-1.5 bg-black/50 text-white rounded-full opacity-0 group-hover/optimg:opacity-100 transition-opacity hover:bg-black/70"
                                                >
                                                    <ZoomInIcon className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between w-full">
                                            <span className="font-semibold">{option}</span>
                                            {isSubmitted && isCorrectAnswer && <CheckIcon className="h-6 w-6 text-green-600" />}
                                            {isSubmitted && !isCorrectAnswer && isSelected && <XMarkIcon className="h-6 w-6 text-red-600" />}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Phần 3: Kết quả và Hành động */}
                    <div className="mt-10 text-center">
                        {isSubmitted ? (
                            <div className="space-y-4">
                                {isCorrect ? (
                                    <div className="p-4 bg-green-100 text-green-800 rounded-lg">
                                        <h4 className="font-bold">Chính xác! Làm tốt lắm!</h4>
                                    </div>
                                ) : (
                                     <div className="p-4 bg-red-100 text-red-800 rounded-lg">
                                        <h4 className="font-bold">Chưa đúng rồi!</h4>
                                        <p className="text-sm mt-1">Đáp án đúng là: <strong>{module.question.correctAnswer}</strong></p>
                                    </div>
                                )}
                                <button onClick={onBack} className="px-8 py-3 bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-800">
                                    Quay lại Lộ trình
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                disabled={selectedOption === null}
                                className="px-8 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 disabled:bg-gray-400"
                            >
                                Kiểm tra
                            </button>
                        )}
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default LearningModuleView;